import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { DateTime } from "luxon"

// Context
import { UserContext } from "../../../context/userContext"

//styling
import assessStyles from "../../../styles/assessments/Assessment.module.css"
import myStyles from "../../../styles/assessments/ParticipantAssessments.module.css"

//PrimeReact
import { Button } from "primereact/button"
import { Paginator } from "primereact/paginator"

//Helpers
import { LoaderMedium } from "../../../components/Loaders"
import { LogError } from "../../../helpers/logger"

//Assessments components:
import { assessmentsApiCompleteListPost } from "../assessmentsApi"
import ParticipantAssessmentFacilitator from "./ParticipantAssessmentFacilitator"
import CustomTable from "../../../components/CustomTable/CustomTable"
import { formatToLocalDate } from "../../../helpers/dateConversion"
import CustomFilter from "../../../components/CustomFilter/CustomFilter"
import {
  FilterDefaults,
  FilterLocation,
  FilterTypes,
} from "../../../components/CustomFilter/FilterConstants"
import { formatCompletedDateToLocaleString } from "../assessmentHelpers"

export default function ParticipantAssessmentsCompleted({
  facilitatorProfiles,
  triggerRefresh,
  onSaveFacilitatorProfile,
  selectedOrganizationId,
  setIsChildLoading,
  onView,
}) {
  const userCtx = useContext(UserContext)
  const [isLoading, setIsLoading] = useState(true)
  const [dataset, setDataset] = useState(undefined)
  const [facilitators] = useState(facilitatorProfiles)

  // Filters & Sorting
  const defaultFilters = [
    {
      key: "search_filter",
      label: "Search",
      placeholder: "Keyword Search",
      defaultValue: FilterDefaults.text,
      currentValue: FilterDefaults.text,
      type: FilterTypes.text,
      location: FilterLocation.bar,
    },
    {
      key: "completed_date_filter",
      label: "Completed Date",
      placeholder: "Completed Date (Range)",
      defaultValue: FilterDefaults.calendar,
      currentValue: FilterDefaults.calendar,
      type: FilterTypes.calendar,
      location: FilterLocation.bar,
      showTime: false,
      showIcon: true,
      rangeSelect: true,
    },
  ]

  const defaultSorting = [
    { name: "Name", code: "name", direction: "ASC", enabled: false },
    {
      name: "Description",
      code: "description",
      direction: "ASC",
      enabled: false,
    },
    { name: "Score", code: "score", direction: "ASC", enabled: false },
    {
      name: "Completed date",
      code: "completedDate",
      direction: "DESC",
      enabled: true,
    },
  ]

  const [assessmentFilters, setAssessmentFilters] = useState(defaultFilters)
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters)
  const [assessmentSorting, setAssessmentSorting] = useState(defaultSorting)
  const [appliedSorting, setAppliedSorting] = useState(defaultSorting)

  // Pagination
  const [pageSize, setPageSize] = useState(10)
  const [currentPage, setCurrentPage] = useState(0)

  // Handle screen resize in realtime
  const screenType = useRef("")
  const screenTypeLoading = useRef(false)

  useEffect(() => {
    const handleResize = () => {
      if (!screenTypeLoading.current) {
        if (window.innerWidth <= 768 && screenType.current !== "mobile") {
          screenTypeLoading.current = true
          screenType.current = "mobile"

          const _filters = JSON.parse(JSON.stringify(assessmentFilters))
          _filters.forEach((f) => {
            f.location = FilterLocation.panel
          })
          setAssessmentFilters(_filters)

          screenTypeLoading.current = false
        } else if (window.innerWidth > 768 && screenType.current !== "normal") {
          screenTypeLoading.current = true
          screenType.current = "normal"

          const _filters = JSON.parse(JSON.stringify(assessmentFilters))
          _filters.forEach((f) => {
            f.location = FilterLocation.bar
          })
          setAssessmentFilters(_filters)

          screenTypeLoading.current = false
        }
      }
    }
    handleResize()

    window.addEventListener("resize", handleResize)
    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener("resize", handleResize)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function getFilterByKey(key) {
    const filter = appliedFilters.find((f) => f.key === key)
    return filter
  }

  const loadAssessments = useCallback(async () => {
    try {
      setIsLoading(true)
      setIsChildLoading(true)

      // Get filter items
      const searchFilter = getFilterByKey("search_filter")
      const completedDateFilter = getFilterByKey("completed_date_filter")

      // Get enabled sorting
      const enabledSortingItem = appliedSorting.find((s) => s.enabled === true)
      const sortField = enabledSortingItem?.code || ""
      const sortOrder = enabledSortingItem?.direction
        ? enabledSortingItem?.direction === "DESC"
          ? "z-a"
          : "a-z"
        : ""

      // Modify completed-date filter dates
      let completedDateFilterFrom, completedDateFilterTo
      if (
        completedDateFilter?.currentValue &&
        completedDateFilter?.currentValue.length === 2
      ) {
        completedDateFilterFrom = DateTime.fromJSDate(
          completedDateFilter.currentValue[0],
        ).toISODate()
        completedDateFilterTo = DateTime.fromJSDate(
          completedDateFilter.currentValue[1],
        ).toISODate()
      }

      let payload = {
        // Filters - Start //
        searchPhrase: searchFilter?.currentValue || "",
        completedFrom: completedDateFilterFrom || null,
        completedTo: completedDateFilterTo || null,
        sortField: sortField,
        sortOrder: sortOrder,
        // Filters - End //
        organizationId: selectedOrganizationId,
        participantId: userCtx.participant_id,
        pageSize: pageSize,
        pageNum: currentPage + 1,
      }

      const result = await assessmentsApiCompleteListPost(
        userCtx.tokens.getAssessmentsToken(),
        payload,
      )

      setDataset(result)
      setIsLoading(false)
      setIsChildLoading(false)
    } catch (error) {
      const message = "Unable to load completed assessments"

      LogError(message, error)
      userCtx.setNotificationError(message)

      setDataset(undefined)
      setIsLoading(false)
      setIsChildLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appliedFilters,
    appliedSorting,
    currentPage,
    pageSize,
    userCtx.participant_id,
    selectedOrganizationId,
    triggerRefresh,
  ])

  useEffect(() => {
    loadAssessments()
  }, [loadAssessments])

  const headers = () => {
    return [
      {
        value: "assessmentName",
        label: "Assessment Name",
        size: 3,
      },
      {
        value: "assessmentDescription",
        label: "Description",
        size: 2,
      },
      {
        value: "score",
        label: "Score",
        action: false,
        size: 2,
      },
      {
        value: "completedDate",
        label: "Completed Date",
        action: false,
        size: 2,
        body: (rowData) => {
          let date = rowData.completedDate

          if (!!date) {
            const dateFromIso = DateTime.fromISO(date, { zone: "utc" })

            date = dateFromIso.toLocal().toLocaleString()
          }

          return <>{formatToLocalDate(date)}</>
        },
      },
      {
        value: "facilitator",
        label: "Facilitator",
        action: false, // this is for the action column to show edit or view
        size: 2,
      },
      {
        value: "action",
        label: "",
        action: true,
        size: 1,
      },
    ]
  }

  const mapOrganization = (organization) => {
    if (!organization) {
      return "Unknown Organization"
    }
    if (organization === false) {
      return "Unknown Organization"
    }
    return organization
  }

  const RenderActionBody = (rowData) => (
    <div className={assessStyles.actionButtonContainer}>
      <Button
        type="button"
        onClick={() => onView(rowData.id)}
        className={assessStyles.actionButton}
        tooltip="View assessment"
        tooltipOptions={{ position: "bottom" }}
      >
        <span className="material-icons">visibility</span>
      </Button>
    </div>
  )

  const mapDataCompletedAssesments = () => {
    if (!dataset?.assessments?.length) {
      return []
    }
    return dataset?.assessments?.map((d) => {
      return {
        assessmentName: mapOrganization(d.assessmentName),
        assessmentDescription: d.assessmentDescription
          ? d.assessmentDescription
          : "-",
        score: d.score,
        completedDate: formatCompletedDateToLocaleString(d.completedDate),
        facilitator: (
          <ParticipantAssessmentFacilitator
            assessment={d}
            facilitatorProfiles={facilitators}
            onSaveFacilitatorProfile={onSaveFacilitatorProfile}
            selectedOrganizationId={selectedOrganizationId}
          />
        ),
        action: RenderActionBody(d),
      }
    })
  }

  return (
    <>
      <div className={myStyles.headerCompletedAssessments}>
        <div className={myStyles.completedLayoutFilters}>
          <CustomFilter
            filters={assessmentFilters}
            setFilters={setAssessmentFilters}
            sorting={assessmentSorting}
            singleSortOnly
            setSorting={setAssessmentSorting}
            applyFilters={() => {
              setCurrentPage(0)
              setAppliedFilters(assessmentFilters)
              setAppliedSorting(assessmentSorting)
            }}
          />
        </div>
        <div className={myStyles.refreshContainer}>
          <Button
            icon="pi pi-sync"
            className={`${assessStyles.button} ${myStyles.refreshButton}`}
            onClick={() => {
              loadAssessments()
            }}
            tooltip={`${"Refresh data"}`}
            tooltipOptions={{ position: "bottom" }}
          />
        </div>
      </div>

      <div className={myStyles.completedLayout}>
        <div className={myStyles.completedLayoutBody}>
          {isLoading ? (
            <LoaderMedium />
          ) : (
            <>
              <CustomTable
                headers={headers()}
                collection={mapDataCompletedAssesments()}
                styles={{
                  paddingBottom: "16px",
                  borderRadius: "12px",
                  marginTop: "0px",
                }}
                tableRowStyles={{ backgroundColor: "#FFFFFF" }}
                disablePagination
              />
              <Paginator
                first={dataset ? (dataset?.pageNum - 1) * dataset?.pageSize : 0}
                rows={dataset?.pageSize || 0}
                totalRecords={dataset?.numTotal || 0}
                style={{ marginBottom: "40px" }} // add som spacing at the bottom of the page
                onPageChange={(setup) => {
                  setPageSize(setup.rows)
                  setCurrentPage(setup.page)
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}
        </div>
      </div>
    </>
  )
}
